import { Component } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../../environments/environment';

interface DeleteAccountResponse {
  isSuccess: boolean;
  message?: string;
  error?: string;
}

@Component({
  selector: 'app-delete-user',
  templateUrl: './delete-user.component.html',
})
export class DeleteUserComponent {
  email: string = '';
  password: string = '';
  error: string = '';
  emailError: string = '';
  passwordError: string = '';
  toastMessage: string = '';
  showConfirmModal: boolean = false;
  showToast: boolean = false;
  isLoading: boolean = false;

  constructor(private http: HttpClient) {}

  onDeleteClick(): void {
    // Clear previous errors
    this.clearErrors();

    // Validate form
    if (!this.validateForm()) {
      return;
    }

    // Show confirmation modal
    this.showConfirmModal = true;
  }

  onConfirmDelete(): void {
    this.showConfirmModal = false;
    this.deleteAccount();
  }

  onCancelDelete(): void {
    this.showConfirmModal = false;
  }

  private validateForm(): boolean {
    let isValid = true;

    if (!this.email.trim()) {
      this.emailError = 'Please enter your email.';
      isValid = false;
    } else if (!this.isValidEmail(this.email)) {
      this.emailError = 'Please enter a valid email address.';
      isValid = false;
    }

    if (!this.password.trim()) {
      this.passwordError = 'Please enter your password.';
      isValid = false;
    }

    return isValid;
  }

  private isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  private clearErrors(): void {
    this.error = '';
    this.emailError = '';
    this.passwordError = '';
  }

  private async deleteAccount(): Promise<void> {
    this.isLoading = true;

    const payload = {
      email: this.email.trim(),
      password: this.password.trim(),
    };

    try {
      const response = await this.http
        .post<DeleteAccountResponse>(
          `${environment.apiUrls.api}/api/users/delete-user`,
          payload
        )
        .toPromise();

      if (response?.isSuccess) {
        this.toastMessage = response.message || 'Account deleted successfully!';
        this.showToastMessage();
        this.clearForm();
      } else {
        this.error = response?.error || 'Error deleting account.';
        this.toastMessage = response?.error || 'Error deleting account.';
        this.showToastMessage();
      }
    } catch (error: any) {
      console.error('Delete account error:', error);

      if (error instanceof HttpErrorResponse) {
        this.error =
          error.error?.message ||
          error.message ||
          'Something went wrong. Please try again.';
      } else {
        this.error = 'Something went wrong. Please try again.';
      }

      this.toastMessage = this.error;
      this.showToastMessage();
    } finally {
      this.isLoading = false;
    }
  }

  private clearForm(): void {
    this.email = '';
    this.password = '';
  }

  private showToastMessage(): void {
    this.showToast = true;
    // Auto hide toast after 5 seconds
    setTimeout(() => {
      this.showToast = false;
    }, 5000);
  }

  closeToast(): void {
    this.showToast = false;
  }
}
